<template>
  <div id="app">
    <!-- MOBILE NAVBAR -->
    <the-navigation-mobile
      :class="{ hide: !showNav }"
      @closeNav="showNav = false"
    ></the-navigation-mobile>
    <!-- NAVBAR -->
    <div class="wrap" :class="{ shrink: showNav }">
      <TheNavigation @hamClicked="showNav = !showNav"></TheNavigation>
      <transition name="fade" mode="out-in">
        <router-view class="main"></router-view>
      </transition>
      <TheFooter />
    </div>
  </div>
</template>

<script>
import TheNavigationMobile from '@/components/TheNavigationMobile.vue';
import TheNavigation from '@/components/TheNavigation.vue';
import TheFooter from '@/components/partials/TheFooter.vue';

export default {
  name: 'App',
  data() {
    return {
      showNav: false,
    };
  },
  components: {
    TheNavigationMobile,
    TheNavigation,
    TheFooter,
  },
  computed: {},
  watch: {
    $route() {
      this.showNav = false;
    },
  },
};
</script>

<style lang="scss">
/*****  VARIABLES *****/
:root {
  /** Colors **/
  --hue-primary: 195;
  --sat-primary: 83%;
  --hue-accent: 33;

  --clr-primary-900: var(--hue-primary) var(--sat-primary) 4%;
  --clr-primary-700: var(--hue-primary) var(--sat-primary) 13%;
  --clr-primary-500: var(--hue-primary) var(--sat-primary) 19%;
  --clr-primary-300: var(--hue-primary) var(--sat-primary) 35%;
  --clr-primary-100: var(--hue-primary) var(--sat-primary) 92%;

  --clr-accent-900: var(--hue-accent) 47% 13%;
  --clr-accent-500: var(--hue-accent) 47% 62%;
  --clr-accent-100: var(--hue-accent) 27% 81%;

  --clr-grey-900: var(--hue-primary) 15% 18%;
  --clr-grey-500: var(--hue-primary) 12% 58%;
  --clr-grey-100: var(--hue-primary) 21% 88%;

  --clr-gradient-500: 90deg #002737 0%, #007aa3 100%;

  /** Typography **/
  --text-size: 16px;

  --ff-cursive: 'Tangerine', cursive;
  --ff-serif: 'Noto Serif', serif;
  --ff-crimson-sans: 'Crimson Pro', serif;
  --ff-source-sans: 'Source Sans Pro', sans-serif;
  --ff-roboto-sans: 'Roboto', sans-serif;

  --fs-4: 0.75rem; // 14px
  --fs-5: 1rem; // 16px

  --fs-3-2: clamp(0.5rem, 0.4022rem + 0.4348vw, 0.75rem); // 12px - 8px
  --fs-4-3: clamp(0.75rem, 0.7011rem + 0.2174vw, 0.875rem); // 14px - 12px
  --fs-5-3: clamp(0.75rem, 0.6522rem + 0.4348vw, 1rem); // 16px - 12px
  --fs-5-4: clamp(0.875rem, 0.7689rem + 0.4717vw, 1rem); // 16px - 14px
  --fs-7-5: clamp(1rem, 0.8533rem + 0.6522vw, 1.375rem); // 22px - 16px
  --fs-10-8: clamp(1.5rem, 1.25rem + 1.1111vw, 2.25rem); // 36px - 24px
  --fs-11-10: clamp(2.25rem, 2rem + 1.1111vw, 3rem); // 48px - 36px

  /** Spacing **/
  --size-4: 0.75rem; // 12px
  --size-5: 1rem; // 16px
  --size-6: 1.5rem; // 24px

  --size-3-2: clamp(0.25rem, 0.1522rem + 0.4348vw, 0.5rem); // 8px - 4px
  --size-4-3: clamp(0.5rem, 0.4022rem + 0.4348vw, 0.75rem); // 12px - 8px
  --size-5-4: clamp(0.75rem, 0.6522rem + 0.4348vw, 1rem); // 16px - 12px
  --size-6-4: clamp(0.75rem, 0.4565rem + 1.3043vw, 1.5rem); // 24px - 12px
  --size-8-6: clamp(1.5rem, 0.913rem + 2.6087vw, 3rem); // 48px - 24px
  --size-9-7: clamp(2rem, 1.2174rem + 3.4783vw, 4rem); // 64px - 32px
  --size-9-8: clamp(3rem, 2.6667rem + 1.4815vw, 4rem); // 64px - 48px
  --size-11-2: clamp(0.25rem, -2.7826rem + 13.4783vw, 8rem); // 128px - 4px
  --size-14-11: clamp(8rem, 1.7391rem + 27.8261vw, 24rem); // 384px - 128px
}

/*****  ROUTER TRANSITION *****/
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.shrink {
  transform: scale(0.9) translateX(200px);
  border-radius: 20px;
  height: 100vh;
  overflow: hidden;
  box-shadow: -5px 5px 20px #333;
  cursor: pointer;
}

.hide {
  display: none;
}

/*****  BASE *****/
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-size: 1rem;
}

body {
  font-family: var(--ff-roboto-sans);
  font-weight: 400;
  line-height: 1.2;
  overflow-x: hidden;
  max-width: 100%;
  background-color: hsl(var(--clr-primary-700));
  color: hsl(var(--clr-accent-100));
}

img {
  max-width: 100%;
}

a {
  text-decoration: none;
  color: var(--clr-primary-100);
}

p {
  font-size: var(--fs-5); /* 16px */
  line-height: 1.2; /* 1.5*16 */
  line-height: 1.4;
}

ul {
  list-style: none;
}

.main {
  // max-width: 1440px;
  margin: 0 auto;
}

/*****  TYPOGRAPHY *****/

.logo {
  font-family: var(--ff-cursive);
  font-style: normal;
  font-weight: 400;

  line-height: 36px;
  letter-spacing: 1px;

  color: hsl(var(--clr-primary-100));
}

.heading-2 {
  font-family: var(--ff-serif);
  font-size: var(--fs-7-5);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1.3;
  color: hsl(var(--clr-accent-100));
}
.heading-3 {
  font-family: var(--ff-roboto-sans);
  font-size: var(--fs-5-3);
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: 0.5px;
  color: hsl(var(--clr-accent-100));
}

/*** GRIDS ***/
.grid-col-12 {
  //  margin: 1.5rem clamp(1rem, 10%, 5rem);
  height: 100%;
  margin: var(--size-7);
  display: grid;
  gap: var(--size-5);
  grid-template-columns: repeat(12, 1fr);
  /*   grid-auto-rows: minmax(100px, auto); */
}

.grid-col-5 {
  height: 100%;
  margin: 40px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, 1fr);
}

.grid-col-6 {
  height: 100%;
  margin: 10px;
  display: grid;
  gap: 5px;
  grid-template-columns: repeat(6, 1fr);
}

// Go through and delete

.section {
  padding: 24px 0;
}

.wrap {
  position: absolute;
  top: 0;
  width: 100%;
  min-height: 100vh;
  transition: 0.3s all ease-in-out;
  background-color: hsl(var(--primary-01));
}

#navigation-icon {
  display: none;
  pointer-events: none;
}

/*****  TYPOGRAPHY *****/
.txt-logo {
  font-family: var(--font-logo);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1.5px;

  &--light {
    color: var(--white);
    font-size: 36px;
  }
}

.paragraph {
  font-family: var(--font-secondary);
  color: var(--text-dark);
}

/***** UTILITIES *****/
.mr-0 {
  margin-right: 0.5px;
}
.mr-1 {
  margin-right: 8px;
}

.ml-1 {
  margin-left: 8px;
}

.mt-1 {
  margin-top: 8px;
}

.mb-0 {
  margin-bottom: 0.5px;
}
.mb-1 {
  margin-bottom: 8px;
}

.mb-2 {
  margin-bottom: 20px;
}
.mb-3 {
  margin-bottom: 32px;
}

/***** MEDIA QUERIES *****/
@media (max-width: 900px) {
  #navigation-icon {
    display: block;
    pointer-events: initial;
  }

  #navigation-icon > i {
    color: var(--clr-primary);
    cursor: pointer;
    font-size: 32px;
    // padding: 2px;
  }

  p {
    font-size: 16px;
  }

  .header {
    padding: 0;
    width: 100%;
  }
}

@media (max-width: 600px) {
  p {
    font-size: 16px;
  }

  .section {
    padding: 24px 0;
  }

  .txt-logo {
    font-size: 28px;
  }
}
</style>
